import {
  buildDiscoverChatbotCardViewModel,
  type DiscoverChatbotCardViewModel,
  type ICharacter,
  readPendingFavorite,
  useOptimisticFavorite,
  useUserInfo,
  useUserSettings,
} from "@next-platform/client-common-core";
import { CharacterCardActions } from "@next-platform/client-common-spicy-ui";
import React, { useCallback, useMemo } from "react";

import CharacterModals from "@/components/search/v2/components/CharacterModals";
import { FEATURE_FLAGS } from "@/constants";
import { useFeatureFlag } from "@/hooks";
import DiscoverChatbotCardView from "@/pages/Discover/components/DiscoverChatbotCardView";
import useDiscoverChatbotCardLabels, { type DiscoverChatbotCardLabels } from "@/pages/Discover/hooks/useDiscoverChatbotCardLabels";
import useDiscoverRowCardActions, { type DiscoverRowCardAction } from "@/pages/Discover/hooks/useDiscoverRowCardActions";
import useDiscoverRowDropdownMenu from "@/pages/Discover/hooks/useDiscoverRowDropdownMenu";

const EMPTY_LIKED_BOTS: readonly string[] = [];

type DiscoverRowCardProps = {
  model: DiscoverChatbotCardViewModel;
  labels: DiscoverChatbotCardLabels;
  isAuthenticated: boolean;
  isFavoritePending: boolean;
  onAction: (action: DiscoverRowCardAction, characterId: string) => void;
  onFavorite: (characterId: string) => void;
};

const DiscoverRowCard = ({ model, labels, isAuthenticated, isFavoritePending, onAction, onFavorite }: DiscoverRowCardProps) => {
  const dropdownMenuItems = useDiscoverRowDropdownMenu({
    characterId: model.characterId,
    onAction,
    isAuthenticated,
    canBlockCreator: model.canBlockCreator,
  });

  const handlePress = useCallback(() => onAction(CharacterCardActions.CHAT, model.characterId), [onAction, model.characterId]);
  const handleFavoritePress = useCallback(() => onFavorite(model.characterId), [onFavorite, model.characterId]);

  return (
    <DiscoverChatbotCardView
      data-testid={`DiscoverChatbotCardView-${model.characterId}`}
      model={model}
      labels={labels}
      dropdownMenuItems={dropdownMenuItems}
      isAuthenticated={isAuthenticated}
      isFavoritePending={isFavoritePending}
      onPress={handlePress}
      onFavoritePress={handleFavoritePress}
    />
  );
};

const MemoizedDiscoverRowCard = React.memo(DiscoverRowCard);

type DiscoverRowCardsProps = {
  characters: readonly ICharacter[];
  onSelectCharacter: (character: ICharacter) => void;
  onLessLikeThis: (character: ICharacter) => void;
};

const DiscoverRowCards = ({ characters, onSelectCharacter, onLessLikeThis }: DiscoverRowCardsProps) => {
  const { blurNSFW, chatLanguage, likedBots } = useUserSettings();
  const { onlyUserId } = useUserInfo();
  const { hasFeatureFlag: hasVoiceAttachmentFlag } = useFeatureFlag(FEATURE_FLAGS.CHARACTER_VOICE_ATTACHMENT);
  const { hasFeatureFlag: isLorebookIndicatorEnabled } = useFeatureFlag(FEATURE_FLAGS.LOREBOOK_PUBLIC);
  const labels = useDiscoverChatbotCardLabels();
  const { handleAction, handleFavorite, isAuthenticated, characterInfo, shareModal, blockModal } = useDiscoverRowCardActions({
    characters,
    onSelectCharacter,
    onLessLikeThis,
  });

  const viewerLikedBots = isAuthenticated && likedBots ? likedBots : EMPTY_LIKED_BOTS;
  const { pendingFavorites, toggleFavorite } = useOptimisticFavorite({ likedBots: viewerLikedBots, onFavorite: handleFavorite });

  const models = useMemo(
    () =>
      characters.map((character) =>
        buildDiscoverChatbotCardViewModel({
          character,
          onlyUserId,
          language: chatLanguage ?? "",
          blurNSFW: Boolean(blurNSFW),
          likedBots: viewerLikedBots,
          hasVoiceAttachment: hasVoiceAttachmentFlag,
          isLorebookIndicatorEnabled,
        }),
      ),
    [characters, onlyUserId, chatLanguage, blurNSFW, viewerLikedBots, hasVoiceAttachmentFlag, isLorebookIndicatorEnabled],
  );

  // The heart follows the pending toggle until the request settles; the store takes over again afterwards.
  const optimisticModels = useMemo(() => {
    if (pendingFavorites.size === 0) {
      return models;
    }

    return models.map((model) => {
      const isOptimisticFavorite = readPendingFavorite({ pendingFavorites, characterId: model.characterId });
      return isOptimisticFavorite === null ? model : { ...model, isFavorite: isOptimisticFavorite };
    });
  }, [models, pendingFavorites]);

  return (
    <>
      {optimisticModels.map((model) => (
        <MemoizedDiscoverRowCard
          key={model.characterId}
          model={model}
          labels={labels}
          isAuthenticated={isAuthenticated}
          isFavoritePending={pendingFavorites.has(model.characterId)}
          onAction={handleAction}
          onFavorite={toggleFavorite}
        />
      ))}
      <CharacterModals characterInfo={characterInfo} shareModal={shareModal} blockModal={blockModal} />
    </>
  );
};

export default React.memo(DiscoverRowCards);
